import { Link } from "react-router-dom";
import { Linkedin, Instagram, Facebook, Mail, Phone, MapPin } from "lucide-react";
import { Logo } from "./Logo";
import { BRAND, CONTACT, SOCIAL } from "../lib/site";

const COLS = [
  { title: "Company", links: [["About Us", "/about"], ["Services", "/services"], ["For Candidates", "/careers"], ["Contact", "/contact"]] },
  { title: "Solutions", links: [["IT Recruitment", "/services/it-recruitment"], ["Non-IT Recruitment", "/services/non-it-recruitment"], ["Staffing Solutions", "/services/staffing-solutions"], ["RPO Services", "/services/rpo-services"]] },
  { title: "Explore", links: [["Industries", "/#industries"], ["For Employers", "/employers"], ["Open Positions", "/careers"], ["Get in Touch", "/contact"]] },
];

const SOCIALS = [
  { Icon: Linkedin, key: "linkedin", href: SOCIAL.linkedin, label: "LinkedIn" },
  { Icon: Instagram, key: "instagram", href: SOCIAL.instagram, label: "Instagram" },
  { Icon: Facebook, key: "facebook", href: SOCIAL.facebook, label: "Facebook" },
];

export const Footer = () => (
  <footer className="relative bg-navy text-white overflow-hidden" data-testid="footer">
    <div className="absolute inset-0 grid-lines pointer-events-none" />
    <div className="absolute inset-0 noise-overlay opacity-[0.04] pointer-events-none" />
    <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pt-20 pb-10">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-4">
          <Logo light imgClassName="h-12 md:h-14 w-auto" />
          <p className="mt-5 text-white/60 max-w-sm">
            {BRAND.tagline} — IT &amp; Non-IT recruitment, staffing and talent solutions for growing businesses.
          </p>
          <ul className="flex gap-3 mt-6">
            {SOCIALS.map(({ Icon, key, href, label }) => (
              <li key={key}>
                <a
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`${BRAND.shortName} on ${label}`}
                  data-testid={`social-${key}`}
                  className="w-10 h-10 rounded-full border border-white/15 flex items-center justify-center text-white/70 hover:bg-sapphire hover:border-sapphire hover:text-white transition-colors"
                >
                  <Icon size={18} aria-hidden="true" />
                </a>
              </li>
            ))}
          </ul>
        </div>

        {COLS.map((c) => (
          <nav key={c.title} className="lg:col-span-2" aria-label={c.title}>
            <h2 className="text-xs uppercase tracking-widest text-white/40 mb-4">{c.title}</h2>
            <ul className="space-y-3">
              {c.links.map(([label, to]) => (
                <li key={label + to}>
                  <Link to={to} className="text-sm text-white/75 hover:text-sapphire transition-colors">{label}</Link>
                </li>
              ))}
            </ul>
          </nav>
        ))}

        <div className="lg:col-span-2">
          <h2 className="text-xs uppercase tracking-widest text-white/40 mb-4">Reach Us</h2>
          <ul className="space-y-3 text-sm text-white/75">
            <li className="flex items-start gap-2">
              <Mail size={16} className="mt-0.5 text-sapphire shrink-0" aria-hidden="true" />
              <a href={`mailto:${CONTACT.email}`} className="hover:text-sapphire transition-colors break-all">{CONTACT.email}</a>
            </li>
            <li className="flex items-start gap-2">
              <Phone size={16} className="mt-0.5 text-sapphire shrink-0" aria-hidden="true" />
              <a href={`tel:${CONTACT.phone.replace(/\s/g, "")}`} className="hover:text-sapphire transition-colors">{CONTACT.phone}</a>
            </li>
            <li className="flex items-start gap-2">
              <MapPin size={16} className="mt-0.5 text-sapphire shrink-0" aria-hidden="true" />
              <address className="not-italic">{CONTACT.location}</address>
            </li>
          </ul>
        </div>
      </div>

      <div className="mt-16 pt-6 border-t border-white/10 flex flex-col sm:flex-row justify-between gap-3 text-xs text-white/40">
        <p>© {new Date().getFullYear()} {BRAND.name}. All rights reserved.</p>
        <p>{BRAND.tagline}</p>
      </div>
    </div>
  </footer>
);
